import React, { useEffect, useState } from 'react';

import { Alert, ListItemText, MenuItem, Select, Stack, Typography } from '@mui/material';

import {
  COMMISSION_RULE_CONDITION_DESCRIPTIONS,
  CommissionOption,
  CommissionsConditionTypes,
  RuleCondition,
  SORTED_COMMISSION_RULE_CONDITION_TYPES,
  ruleConditionsToTextMap,
} from '~/consts/agentHub';

import AgentHubCommissionsAffiliationInput from './AgentHubCommissionsAffiliationInput';
import AgentHubCommissionsAgencyInput from './AgentHubCommissionsAgencyInput';
import AgentHubCommissionsAgentInput from './AgentHubCommissionsAgentInput';
import AgentHubCommissionsBundleTotalPriceInput from './AgentHubCommissionsBundleTotalPrice';
import AgentHubCommissionsCruiseLineInput from './AgentHubCommissionsCruiseLineInput';
import AgentHubCommissionsDateRangeInput from './AgentHubCommissionsDateRangeInput';
import AgentHubCommissionsFlightLocationInput from './AgentHubCommissionsFlightLocationInput';
import AgentHubCommissionsLocationInput from './AgentHubCommissionsLocationInput';
import AgentHubCommissionsMarginInput from './AgentHubCommissionsMarginInput';
import AgentHubCommissionsMinTotalSpendInput from './AgentHubCommissionsMinTotalSpendInput';
import AgentHubCommissionsProductTypeInput from './AgentHubCommissionsProductTypeInput';
import AgentHubCommissionsProviderInput from './AgentHubCommissionsProviderInput';
import AgentHubCommissionsTextInput from './AgentHubCommissionsTextInput';
import AgentHubCommissionsTierInput from './AgentHubCommissionsTierInput';
import AgentHubCommissionsUserTagsInput from './AgentHubCommissionsUserTagsInput';
import FlightCarriersInput from './FlightCarriersInput';

interface Props {
  addCondition: (condition: RuleCondition, replace?: boolean) => void;
  ruleConditions: CommissionOption;
  conditionType: CommissionsConditionTypes;
  handleChangeCondition: (prevType: CommissionsConditionTypes, type: CommissionsConditionTypes) => void;
  /** Only required when this row’s condition is (or can become) `flightCarrier`. */
  brand?: string;
  onFlightCarrierChange?: (next: CommissionOption) => void;
}

export function AddNewConditionForm({
  conditionType,
  ruleConditions,
  addCondition,
  handleChangeCondition,
  brand,
  onFlightCarrierChange,
}: Props) {
  const [selectedConditionType, setSelectedConditionType] = useState(conditionType || null);
  const selectedConditionDescription = selectedConditionType
    ? COMMISSION_RULE_CONDITION_DESCRIPTIONS[selectedConditionType]
    : undefined;

  useEffect(() => {
    setSelectedConditionType((currentConditionType) =>
      currentConditionType === conditionType ? currentConditionType : conditionType,
    );
  }, [conditionType]);

  return (
    <Stack direction="column" spacing={1}>
      <Select
        value={selectedConditionType}
        name="condition-type"
        labelId="agent-hub-commissions-condition-type-input-label"
        label="Condition Type"
        sx={{ borderRadius: 0.5 }}
        onChange={(event) => {
          const newConditionType = event.target.value as CommissionsConditionTypes;
          if (newConditionType === selectedConditionType) {
            return;
          }

          setSelectedConditionType(newConditionType);
          handleChangeCondition(conditionType, newConditionType);
        }}
        renderValue={(value) => ruleConditionsToTextMap.get(value)}
      >
        {SORTED_COMMISSION_RULE_CONDITION_TYPES.map((type) => (
          <MenuItem key={type.value} value={type.value}>
            <ListItemText primary={type.label} />
          </MenuItem>
        ))}
      </Select>

      {selectedConditionType === 'agency' && (
        <AgentHubCommissionsAgencyInput existingConditions={ruleConditions} setCreatedConditions={addCondition} />
      )}

      {selectedConditionType === 'agent' && (
        <AgentHubCommissionsAgentInput existingConditions={ruleConditions} setCreatedConditions={addCondition} />
      )}

      {selectedConditionType === 'tag' && (
        <AgentHubCommissionsUserTagsInput existingConditions={ruleConditions} setCreatedConditions={addCondition} />
      )}

      {selectedConditionType === 'location' && (
        <AgentHubCommissionsLocationInput existingConditions={ruleConditions} setCreatedConditions={addCondition} />
      )}

      {selectedConditionType === 'flightDeparture' && (
        <AgentHubCommissionsFlightLocationInput
          variant="flightDeparture"
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
        />
      )}

      {selectedConditionType === 'flightArrival' && (
        <AgentHubCommissionsFlightLocationInput
          variant="flightArrival"
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
        />
      )}

      {selectedConditionType === 'bundledWithOffer' && (
        <AgentHubCommissionsTextInput
          conditionType="bundledWithOffer"
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
        />
      )}

      {selectedConditionType === 'flightClass' && (
        <AgentHubCommissionsTextInput
          conditionType="flightClass"
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
        />
      )}

      {selectedConditionType === 'bundledWithTypes' && (
        <AgentHubCommissionsProductTypeInput
          ruleType="bundledWithTypes"
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
        />
      )}

      {selectedConditionType === 'productType' && (
        <AgentHubCommissionsProductTypeInput existingConditions={ruleConditions} setCreatedConditions={addCondition} />
      )}

      {selectedConditionType === 'flightCarrier' &&
        (brand ? (
          <FlightCarriersInput
            brand={brand}
            value={ruleConditions}
            onChange={onFlightCarrierChange ?? (() => undefined)}
          />
        ) : (
          <Alert severity="warning" variant="outlined">
            Select a tenant brand to search airlines.
          </Alert>
        ))}

      {selectedConditionType === 'vendor' && (
        <AgentHubCommissionsTextInput
          conditionType="vendor"
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
        />
      )}

      {selectedConditionType === 'provider' && (
        <AgentHubCommissionsProviderInput existingConditions={ruleConditions} setCreatedConditions={addCondition} />
      )}

      {selectedConditionType === 'affiliation' && (
        <AgentHubCommissionsAffiliationInput existingConditions={ruleConditions} setCreatedConditions={addCondition} />
      )}

      {selectedConditionType === 'offer' && (
        <AgentHubCommissionsTextInput
          conditionType="offer"
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
        />
      )}

      {selectedConditionType === 'startDate' && (
        <AgentHubCommissionsDateRangeInput
          conditionType="startDate"
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
          description="Dates are interpreted using Sydney time (Australia/Sydney)."
        />
      )}

      {selectedConditionType === 'endDate' && (
        <AgentHubCommissionsDateRangeInput
          conditionType="endDate"
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
          description="Dates are interpreted using Sydney time (Australia/Sydney)."
        />
      )}

      {selectedConditionType === 'flightDepartureDate' && (
        <AgentHubCommissionsDateRangeInput
          conditionType="flightDepartureDate"
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
        />
      )}

      {selectedConditionType === 'flightReturnDate' && (
        <AgentHubCommissionsDateRangeInput
          conditionType="flightReturnDate"
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
        />
      )}

      {selectedConditionType === 'margin' && (
        <AgentHubCommissionsMarginInput existingConditions={ruleConditions} setCreatedConditions={addCondition} />
      )}

      {selectedConditionType === 'bundleTotalPrice' && (
        <AgentHubCommissionsBundleTotalPriceInput
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
        />
      )}

      {selectedConditionType === 'minTotalSpend' && (
        <AgentHubCommissionsMinTotalSpendInput
          existingConditions={ruleConditions}
          setCreatedConditions={addCondition}
        />
      )}

      {selectedConditionType === 'tier' && (
        <AgentHubCommissionsTierInput existingConditions={ruleConditions} setCreatedConditions={addCondition} />
      )}

      {selectedConditionType === 'cruiseLine' && (
        <AgentHubCommissionsCruiseLineInput existingConditions={ruleConditions} setCreatedConditions={addCondition} />
      )}

      {selectedConditionDescription && (
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            {selectedConditionDescription.description}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Applies to: {selectedConditionDescription.appliesTo}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
